import { FC, useEffect, type JSX } from 'react';
import { useDispatch } from 'react-redux';

import AuthenticatedPageWrapper from '@/ui/layouts/authenticatedPageWrapper/AuthenticatedPageWrapper';

import * as servicePlansActions from '@payaca/store/servicePlans/servicePlansActions';

import { extractErrorMessage } from '@/api/extractErrorMessage';
import useCreateProjectForServicePlanPeriod from '@/api/mutations/servicePlans/useCreateProjectForServicePlanPeriod';
import { ServicePlanPeriodStatusBadge } from '@/ui/components/servicePlanPeriodStatusBadge/ServicePlanPeriodStatusBadge';
import { useRegion } from '@/api/rest/users/useRegion';
import { Button, Text } from '@payaca/ambrosia';
import {
  DateFormats,
  getInternationalMomentDateFormatByRegion,
} from '@payaca/helpers/internationalHelper';
import {
  useServicePlan,
  useServicePlanPeriod,
} from '@payaca/store/hooks/appState';
import moment from 'moment-timezone';
import { Redirect, useHistory } from 'react-router-dom';
import './ServicePlanPeriodPage.sass';

type Props = {
  servicePlanPeriodId: string;
};

const ServicePlanPeriodPage: FC<Props> = ({
  servicePlanPeriodId,
}: Props): JSX.Element | null => {
  const dispatch = useDispatch();
  const history = useHistory();

  const servicePlanPeriod = useServicePlanPeriod(servicePlanPeriodId);
  const servicePlan = useServicePlan(servicePlanPeriod?.servicePlanPublicId);

  const createProject = useCreateProjectForServicePlanPeriod({
    options: {
      onSuccess: (data) => {
        history.replace(
          `/projects/${data.createProjectForServicePlanPeriod.publicId}/commitments`
        );
      },
      onError: (error) => {
        // Race (another tab created the project) — refresh the period so the
        // projectPublicId redirect branch below takes over on next render.
        if (extractErrorMessage(error).code === 'CONFLICT') {
          dispatch(
            servicePlansActions.getServicePlanPeriod.request({
              publicId: servicePlanPeriodId,
            })
          );
        }
      },
    },
  });

  useEffect(() => {
    dispatch(
      servicePlansActions.getServicePlanPeriod.request({
        publicId: servicePlanPeriodId,
      })
    );
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useEffect has a missing dependency: 'dispatch'
  }, [servicePlanPeriodId]);

  useEffect(() => {
    if (servicePlanPeriod) {
      dispatch(
        servicePlansActions.getServicePlan.request({
          publicId: servicePlanPeriod.servicePlanPublicId,
        })
      );
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useEffect has missing dependencies: 'dispatch', 'servicePlanPeriod', and 'servicePlanPeriod.servicePlanPublicId'
  }, [servicePlanPeriodId]);

  const region = useRegion();

  const timestampShortDateRegionalFormat =
    getInternationalMomentDateFormatByRegion(DateFormats.MID, region);

  if (servicePlanPeriod?.projectPublicId) {
    return (
      <Redirect
        to={`/projects/${servicePlanPeriod.projectPublicId}/commitments`}
      />
    );
  }

  return (
    <AuthenticatedPageWrapper
      className="service-plan-period-page"
      breadcrumbItems={[
        { label: 'Back', to: '/service-plans/service-periods' },
        { label: 'Service plan period' },
      ]}
    >
      <div className="service-plan-period-page-header flex min-h-16 items-center gap-2 px-8 py-4">
        <h1>Service plan period</h1>
        {servicePlanPeriod && (
          <ServicePlanPeriodStatusBadge status={servicePlanPeriod.status} />
        )}
      </div>
      {servicePlanPeriod && (
        <div className="p-4">
          <div>
            <span className="service-plan-name">{servicePlan?.name}</span>
            <h2>{servicePlanPeriod.customerName}</h2>
            <dl className="service-plan-details">
              <dt>Start</dt>
              <dd>
                {moment(servicePlanPeriod.startsAt).format(
                  timestampShortDateRegionalFormat
                )}
              </dd>
              <dt>End</dt>
              <dd>
                {moment(servicePlanPeriod.endsAt).format(
                  timestampShortDateRegionalFormat
                )}
              </dd>
            </dl>
          </div>

          <div className="mt-6 space-y-2">
            <Text>
              To arrange commitments you must first create a project for the
              service period.
            </Text>
            <Button
              isProcessing={createProject.isPending}
              onClick={() => createProject.mutate({ servicePlanPeriodId })}
            >
              Create project
            </Button>
          </div>
        </div>
      )}
    </AuthenticatedPageWrapper>
  );
};

export default ServicePlanPeriodPage;
